import { useCallback, useEffect, useRef } from "react";

/**
 * "Arrastar o fundo pra rolar" (grab-to-pan) horizontal do quadro Kanban:
 * clicar e segurar num espaço VAZIO (entre/ao redor das colunas) e arrastar pro
 * lado rola o quadro — igual a arrastar o fundo no Trello.
 *
 * Não conflita com o drag-and-drop nem com cliques: só inicia o pan quando o
 * alvo do mousedown NÃO é um card/handle da lib (@hello-pangea/dnd marca com
 * atributos data-rbd-*) nem um controle interativo (botão, input, link, etc.).
 * É só mouse — no touch o swipe nativo do container já rola.
 */
export function useDragToScroll(elRef: React.RefObject<HTMLElement | null>) {
  const st = useRef<{ startX: number; startScroll: number } | null>(null);
  // Guarda as funções exatas registradas, pra removê-las sem ciclo de deps.
  const handlers = useRef<{ move?: (e: MouseEvent) => void; up?: () => void }>({});

  const onUp = useCallback(() => {
    if (!st.current) return;
    st.current = null;
    const h = handlers.current;
    if (h.move) window.removeEventListener("mousemove", h.move);
    if (h.up) {
      window.removeEventListener("mouseup", h.up);
      window.removeEventListener("blur", h.up);
    }
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const onMove = useCallback((e: MouseEvent) => {
    const el = elRef.current;
    const s = st.current;
    if (!el || !s) return;
    // Botão já foi solto (inclusive FORA da janela, quando o mouseup se perde)?
    // Encerra — evita o "pan grudado" e o cursor/seleção travados globalmente.
    if (e.buttons === 0) { onUp(); return; }
    e.preventDefault(); // impede seleção de texto durante o arrasto
    el.scrollLeft = s.startScroll - (e.clientX - s.startX);
  }, [elRef, onUp]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // só botão esquerdo
    const el = elRef.current;
    if (!el) return;
    const alvo = e.target as HTMLElement;
    // Clicou num card/handle da lib de DnD? Deixa a lib cuidar (não paneia).
    if (alvo.closest("[data-rbd-draggable-id],[data-rbd-drag-handle-draggable-id]")) return;
    // Clicou num controle interativo? Não paneia (deixa o clique acontecer).
    if (alvo.closest('button,a,input,select,textarea,label,[role="button"],[contenteditable="true"]')) return;
    st.current = { startX: e.clientX, startScroll: el.scrollLeft };
    handlers.current = { move: onMove, up: onUp };
    window.addEventListener("mousemove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    // Fallback: se a janela perde o foco (alt-tab) no meio do pan, o mouseup
    // pode nunca chegar — o blur garante a limpeza do estado global.
    window.addEventListener("blur", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  }, [elRef, onMove, onUp]);

  // Limpeza se desmontar no meio de um pan.
  useEffect(() => () => onUp(), [onUp]);

  return { onMouseDown };
}
